/* eslint-disable max-lines-per-function */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef, useState } from "react";
import ReactGA from "react-ga4";
import { Button, Col, Row, Space } from "antd";
import Heading from "components/Common/Heading/Heading";
import "./ShareDesign.scss";
import BillingInformation from "./BillingInformation";
import ShippingInformation from "./ShippingInformation";
import PlayerRoster from "./PlayerRoster";
import AdditionalInformation from "./AdditionalInformation";
import { useNavigate, useSearchParams } from "react-router-dom";
import { IPlayerRoster, ShareDesignDetails } from "interfaces/ShareDesign/IShareDesign";
import { ROUTES_PATH } from "routes/Routes.Constant";
import { saveShareCustomDesign } from "api/services/shareDesign.api";
import { AxiosError } from "axios";
import { notificationController } from "components/Common/Notification/Notification";
import { useDispatch } from "react-redux";
import { setPlayerRoster } from "store/slices/playerRoster.slice";
import { getProductsByDesignId } from "api/services/product.api";
import { SelectedCustomProducts } from "interfaces/ApiInterface/IProductGroupList";
import { useAppSelector } from "hooks/reduxHooks";

const ShareDesign = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const fileInputRef = useRef<any>(null);
  const [designIds, setDesignIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const userId: number = JSON.parse(localStorage.getItem("UserRecord") || "")?.UserId;
  const publishCatalogId = useAppSelector((state: any) => state.landingPage.publishCatalogId);

  const [billingInformation, setBillingInformation] = useState<ShareDesignDetails>({
    purchaseOrderNo: "",
    billingAccountNo: "",
    shippingAccountNo: "",
    shippingAddress: "",
    confirmationEmailAddress: "",
    notes: "",
    playerRosters: [
      {
        key: 1,
        playerName: "",
        uniformNo: "",
        jerseySize: "",
        pantSize: "",
        helmetSize: "",
        inseam: "",
        CreatedBy: userId,
        CreatedDate: new Date().toISOString(),
      },
    ],
  });

  const selectedProducts: SelectedCustomProducts[] = JSON.parse(
    localStorage.getItem("selectedItemsToShare") || ""
  );

  const onHandleBillingForm = (e: any) => {
    setBillingInformation({ ...billingInformation, [e.target.name]: e.target.value });
  };

  const getFilteredPlayerRoaster = (playerRoasters: any) =>
    playerRoasters?.filter((obj: any) => {
      if (
        obj.helmetSize ||
        obj.inseam ||
        obj.jerseySize ||
        obj.pantSize ||
        obj.playerName ||
        obj.uniformNo
      ) {
        return true;
      }
      return false;
    });

  const onSubmitBillingInformation = () => {
    const filteredPlayerRoaster = getFilteredPlayerRoaster(billingInformation?.playerRosters);

    const isNumberMoreThanTwoDigits = filteredPlayerRoaster.some((item: IPlayerRoster) => {
      const regex = /^(?:0\d?|0[02]|(?:\d|[1-9]\d))$/;
      return item.uniformNo && !regex.test(item.uniformNo);
    });

    if (!isNumberMoreThanTwoDigits) {
      setLoading(!loading);
      const meta = {
        designId: designIds.join(", "),
        CreatedBy: userId,
        CreatedDate: new Date().toISOString(),
      };

      const billingBody = { ...billingInformation, playerRosters: filteredPlayerRoaster };

      saveShareCustomDesign({ ...billingBody, ...meta })
        .then((response: any) => {
          if (response.status === 200 && response?.data?.IsSuccess) {
            dispatch(setPlayerRoster(billingBody));
            let query = "?";
            for (let i = 0; i < designIds.length; i++) {
              query += `design${i}=${designIds[i]}${designIds.length !== i + 1 ? "&" : ""}`;
            }
            setLoading(!loading);
            navigate(`${ROUTES_PATH.SHARE_DESIGN_2}${query}`);
          }
        })
        .catch((error: AxiosError) => {
          setLoading(!loading);
          notificationController.error({
            message: error.message,
          });
          throw error;
        });
    }
  };

  /* updating the data change on input field */
  const onHandleTextField = (record: any, data: any) => {
    const playerList = [...billingInformation.playerRosters];
    const newData = playerList?.find((item: IPlayerRoster) => item?.key === record?.key);
    if (newData) {
      const rowObj = { ...newData, ...data };
      const notUpdated = billingInformation?.playerRosters?.filter(
        (item: IPlayerRoster) => item?.key !== rowObj?.key
      );
      if (!notUpdated.length) {
        setBillingInformation({
          ...billingInformation,
          playerRosters: [rowObj].sort((a: any, b: any) => a.key - b.key),
        });
      } else {
        setBillingInformation({
          ...billingInformation,
          playerRosters: [rowObj, ...notUpdated].sort((a: any, b: any) => a.key - b.key),
        });
      }
    }
  };

  const getMaximumIndex = (): number =>
    billingInformation.playerRosters.reduce(
      (acc: number, curr: IPlayerRoster) => (curr?.key > acc ? curr.key : acc),
      0
    );

  /* adding new blank row in the table */
  const handleAdd = () => {
    ReactGA.event({
      category: "new_player_added",
      action: "add new player button clicked",
      label: "add new player button clicked"
    });
    const newData: IPlayerRoster = {
      key: getMaximumIndex() + 1,
      playerName: "",
      uniformNo: "",
      jerseySize: "",
      pantSize: "",
      helmetSize: "",
      inseam: "",
      CreatedBy: userId,
      CreatedDate: new Date().toISOString(),
    };
    setBillingInformation({
      ...billingInformation,
      playerRosters: [...billingInformation.playerRosters, newData],
    });
  };

  /* getting select field data */
  const onChangeSelect = (selectType: string, key: number, data: string) => {
    const newData = billingInformation?.playerRosters.map((item: IPlayerRoster) => {
      if (+item?.key === +key) {
        return { ...item, [selectType]: data };
      }
      return item;
    });
    setBillingInformation({ ...billingInformation, playerRosters: newData } as ShareDesignDetails);
  };

  /* delete row from the table */
  const onHandleDelete = (key: number) => {
    const newData = billingInformation?.playerRosters?.filter(
      (item: IPlayerRoster) => item.key !== key
    );
    setBillingInformation({
      ...billingInformation,
      playerRosters: newData,
    });
  };

  /* save the table data onEnter or onBlur*/
  const onHandleSave = (row: IPlayerRoster) => {
    const newData = [...billingInformation.playerRosters];
    const index = newData.findIndex((item: IPlayerRoster) => row.key === item.key);
    const item = newData[index];
    newData.splice(index, 1, {
      ...item,
      ...row,
    });
    setBillingInformation({
      ...billingInformation,
      playerRosters: newData,
    });
  };

  const handleCSVUpload = async (e: any) => {
    e.preventDefault();
    const file = e.target.files[0];
    const fileSplit = file?.name.split(".");
    const ext = fileSplit[fileSplit.length - 1].toLowerCase();
    if (file?.type !== "text/csv" && ext !== "csv") {
      notificationController.error({
        message: "Only csv file is allowed",
      });
      return false;
    }

    const fileUrl = URL.createObjectURL(file);
    const response = await fetch(fileUrl);
    const text = await response.text();
    const lines = text.split("\n");
    const rawLines = lines.filter((item: string) => item !== "");
    const rawData = rawLines.map((line: string) => line.replace("\r", "").split(","));
    const jsonArray: IPlayerRoster[] = [];

    for (let i = 1; i < rawData.length; i++) {
      const jsonObj: any = {
        key: getMaximumIndex() + i,
        playerName: rawData[i][0],
        uniformNo: rawData[i][1],
        jerseySize: rawData[i][2],
        pantSize: rawData[i][3],
        helmetSize: rawData[i][4],
        inseam: rawData[i][5],
        CreatedBy: userId,
        CreatedDate: new Date().toISOString(),
      };
      jsonArray.push(jsonObj);
    }

    const data = [...billingInformation.playerRosters, ...jsonArray];

    if (billingInformation?.playerRosters.length) {
      setBillingInformation({
        ...billingInformation,
        playerRosters: getFilteredPlayerRoaster(data),
      });
    } else {
      setBillingInformation({
        ...billingInformation,
        playerRosters: getFilteredPlayerRoaster(data),
      });
    }
    fileInputRef.current.value = null;
  };

  useEffect(() => {
    if (publishCatalogId) {
      getProductsByDesignId(selectedProducts, userId, publishCatalogId)
        .then((res: any) => {
          if (res && res?.data && res?.data?.ProductList.length) {
            const arr1 = res?.data?.ProductList.map((item: any) => item.DesignId);
            const arr2 = Object.values(Object.fromEntries(searchParams));
            const allElementsPresent = arr2.every((element: string) => arr1.includes(element));
            if (allElementsPresent) {
              setDesignIds(arr2);
            } else {
              navigate(ROUTES_PATH.HOMEPAGE);
            }
          } else {
            navigate(ROUTES_PATH.HOMEPAGE);
          }
        })
        .catch((error: Error) => {
          throw error;
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publishCatalogId]);

  return (
    <div className="page-content-wrapper p-30">
      <Row gutter={[30, 15]} className="global-form-container">
        <Col span={24}>
          <Heading titleText="Share Selected Design" titleClass="main-heading global-heading" />
        </Col>
        <Col span={8} xs={24} md={24} xxl={5} lg={5}>
          <div className="form-wrapper share-selected-design">
            <BillingInformation
              onHandleBillingForm={onHandleBillingForm}
              billingInformation={billingInformation}
            />
            <ShippingInformation onHandleBillingForm={onHandleBillingForm} />
            <AdditionalInformation
              onHandleBillingForm={onHandleBillingForm}
              billingInformation={billingInformation}
            />
          </div>
        </Col>
        <Col span={16} xs={24} md={24} xxl={19} lg={19} className="player-roster-wrapper">
          <PlayerRoster
            products={billingInformation?.playerRosters}
            onHandleTextField={onHandleTextField}
            onHandleAdd={handleAdd}
            onChangeSelect={onChangeSelect}
            onHandleDelete={onHandleDelete}
            onHandleSave={onHandleSave}
            handleCSVUpload={handleCSVUpload}
            fileInputRef={fileInputRef}
          />
        </Col>
        <Col span={24} className="global-action share-design-action">
          <Space size={"middle"}>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              data-test-selector={"btnNext"}
              onClick={onSubmitBillingInformation}
              className={loading ? "btn-disabled next-btn" : "next-btn"}
            >
              Next
            </Button>
            <Button
              className="cancel-btn"
              data-test-selector={"btnCancel"}
              onClick={() => navigate(ROUTES_PATH.HOMEPAGE)}
            >
              Cancel
            </Button>
          </Space>
        </Col>
      </Row>
    </div>
  );
};

export default ShareDesign;
